<template>
    <div class="tw-w-106 tw-max-w-full tw-mx-2 xl:tw-w-130 xl:tw-mx-0">
        <div class="tw-p-8 tw-bg-white tw-rounded-xl">
            <h1
                class="tw-p-0 tw-m-0 tw-text-2xl tw-mb-4 tw-pb-4 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-items-center tw-font-bold"
            >
                <template v-if="authStrategy.teamName">
                    {{ $t("auth.login.titleKnownUser") }}
                    <span class="tw-text-indigo-500 tw-ml-2">{{ authStrategy.teamName }}</span>
                    <img src="@/img/icons/hand_small.png" alt="" class="tw-w-8 tw-ml-2" />
                </template>
                <template v-else>
                    {{ $t("auth.login.title") }}
                    <img src="@/img/icons/hand_small.png" alt="" class="tw-w-8 tw-ml-2" />
                </template>
            </h1>

            <template v-if="allowGoogleSignIn">
                <div class="tw-flex tw-items-center tw-justify-center">
                    <l-sign-in-with-google class="tw-my-4" />
                </div>

                <div class="tw-my-4 tw-relative">
                    <div class="tw-absolute tw-inset-0 tw-flex tw-items-center" aria-hidden="true">
                        <div
                            class="tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-gray-200"
                        ></div>
                    </div>
                    <div class="tw-relative tw-flex tw-justify-center tw-text-sm">
                        <span class="tw-px-2 tw-bg-white tw-text-gray-500">
                            {{ $t("auth.generic.orContinueWith") }}
                        </span>
                    </div>
                </div>
            </template>

            <form class="tw-space-y-8" @submit.prevent="onSubmit">
                <div v-show="authStrategy.strategy !== 'saml' || isForcedReturn">
                    <l-input-text
                        id="username"
                        ref="emailInput"
                        name="username"
                        :label="$t('auth.generic.labels.email')"
                        type="text"
                        :value.sync="form.email"
                        class="tw"
                        data-test-id="login-email"
                    />
                </div>

                <l-base-button
                    v-if="loginStep === 1"
                    :disabled="isLoading"
                    :button-types="['primary', 'raised']"
                    type="button"
                    data-test-id="form-continue"
                    @click.native="onContinue"
                >
                    {{ $t("auth.login.formContinueAction") }}
                </l-base-button>

                <div v-if="isPasswordInputVisible">
                    <l-input-text
                        :label="$t('auth.generic.labels.password')"
                        type="password"
                        :value.sync="form.password"
                        class="tw"
                        data-test-id="login-password"
                    />

                    <div class="tw-text-right tw-mt-2">
                        <a
                            :href="
                                `#/auth/password-reset-request?email=${encodeURIComponent(
                                    form.email,
                                )}`
                            "
                            data-test-id="reset-password-request-link"
                        >
                            {{ $t("auth.login.forgotPasswordLink") }}
                        </a>
                    </div>
                </div>

                <l-text-alert
                    v-if="hasError"
                    type="danger"
                    data-test-id="error"
                    :text="errorMessage"
                />

                <div
                    v-if="loginStep == 2 && authStrategy && authStrategy.strategy === 'saml'"
                    class="tw-text-gray-400"
                >
                    <i18n path="auth.login.samlEmailHint">
                        <template v-slot:email>
                            <b>{{ form.email }}</b>
                        </template>
                        <template v-slot:linkToChange>
                            <a
                                href="#"
                                data-test-id="change-email-link"
                                @click.prevent="onForceLoginStepOne"
                                >{{ $t("auth.login.linkToChange") }}</a
                            >
                        </template>
                    </i18n>
                </div>

                <l-base-button
                    v-if="loginStep == 2"
                    :disabled="isSubmitButtonDisabled"
                    :button-types="['primary', 'raised']"
                    type="submit"
                    data-test-id="form-submit"
                    class="tw-flex"
                >
                    <template v-if="authStrategy && authStrategy.strategy === 'saml'">
                        <i class="fa fa-lock tw-mr-1" aria-hidden="true"></i>
                        <span>{{ $t("auth.login.formSubmitActionSaml") }}</span>
                    </template>
                    <template v-else>
                        {{ $t("auth.login.formSubmitAction") }}
                    </template>
                </l-base-button>

                <div
                    v-if="!hasEnforcedSSO && authStrategy.strategy === 'saml' && loginStep == 2"
                    class="tw-text-center tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-gray-50"
                >
                    <a
                        href="#"
                        data-test-id="force-local-auth-link"
                        @click.prevent="onForceLocalLogin"
                    >
                        {{ $t("auth.login.forceLocalAuth") }}
                    </a>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import LInputText from "@/components/LInputText";
import LTextAlert from "@/components/LTextAlert";
import LSignInWithGoogle from "@/components/LAuth/LSignInWithGoogle";
import LBaseButton from "@/components/LBaseButton";

export default {
    components: {
        LInputText,
        LTextAlert,
        LSignInWithGoogle,
        LBaseButton,
    },
    data() {
        return {
            loginStep: 1,
            hasError: false,
            errorMessage: "",
            isLoading: false,
            form: {
                email: "",
                password: "",
            },
            lastValidEmailDomain: "",
            isForcedReturn: false,
            isForcedLocalAuthenticationStrategy: false,
        };
    },
    computed: {
        ...mapGetters({
            authStrategy: "auth/authStrategy",
        }),
        isSubmitButtonDisabled() {
            return !this.authStrategy?.strategy || this.isLoading;
        },
        isPasswordInputVisible() {
            return this.loginStep === 2 && this.authStrategy?.strategy !== "saml";
        },
        allowGoogleSignIn() {
            return this.authStrategy?.strategy !== "saml";
        },
        hasEnforcedSSO() {
            return this.authStrategy?.strategy === "saml" && this.authStrategy?.enforced;
        },
    },
    watch: {
        "form.email": debounce(async function(newValue = "") {
            const emailParts = newValue.split("@");
            const emailDomain = (emailParts?.[1] ?? "").toLowerCase();

            // conditionally re-trigger authentication strategy check
            if (
                this.loginStep !== 2 ||
                this.$refs.emailInput?.validate() === false ||
                !this.lastValidEmailDomain ||
                emailDomain === this.lastValidEmailDomain ||
                !emailDomain ||
                !emailParts?.[0].trim()
            ) {
                // We only do that in step 2 (normal or forced flow) if
                // the email is changed in comparison to the previous state.
                return;
            }

            try {
                await this.$store.dispatch("auth/getAuthStrategy", {
                    payload: { username: newValue },
                });
            } catch {
                // we proceed with the regular login form
            }
        }, 500),
    },
    methods: {
        // Certain interactions require us to take a snapshot of the last valid
        // input to trigger certain scenario's like another auth stretgy check
        takeEmailDomainSnapshot() {
            this.lastValidEmailDomain = (this.form.email?.split("@")?.[1] ?? "").toLowerCase();
        },
        onForceLoginStepOne() {
            this.isForcedReturn = true;
            this.loginStep = 1;

            this.takeEmailDomainSnapshot();
        },
        onForceLocalLogin() {
            this.$store.commit("auth/setAuthStrategy", {
                ...this.authStrategy,
                strategy: "local",
            });
            this.isForcedLocalAuthenticationStrategy = true;

            this.takeEmailDomainSnapshot();
        },
        async onContinue() {
            if (this.$refs.emailInput?.validate() === false) return;
            this.hasError = false;
            this.isLoading = true;

            // determine the user's auth strategy
            try {
                await this.$store.dispatch("auth/getAuthStrategy", {
                    payload: { username: this.form.email },
                });
                this.isForcedReturn = false;
                this.loginStep = 2;

                this.takeEmailDomainSnapshot();
            } catch (err) {
                this.hasError = true;
                this.errorMessage = err.response?.data?.message ?? err.message;
            } finally {
                this.isLoading = false;
            }
        },
        async onSubmit() {
            if (this.loginStep === 1) {
                this.onContinue();
                return;
            }

            this.hasError = false;
            this.isLoading = true;

            try {
                switch (this.authStrategy?.strategy) {
                    case "saml":
                        if (!this.authStrategy?.team) return;
                        window.location = `/api/users/auth/saml/${this.authStrategy.team}/login`;
                        return;
                    default:
                        await this.$store.dispatch("auth/performLocalAuth", {
                            payload: { username: this.form.email, password: this.form.password },
                        });
                }
            } catch (err) {
                this.hasError = true;
                this.errorMessage = err.response?.data?.message ?? err.message;
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
